/**
 * Rasteransicht des Editors.
 *
 * Die Ansicht erzeugt das DOM, spiegelt den Zustand des Controllers und
 * leitet Benutzeraktionen ausschliesslich an den Controller weiter.
 * Sie enthaelt selbst keine fachliche Logik.
 */

import type { EditorController } from '../app/EditorController';
import {
  INSTRUMENT_LABELS,
  INSTRUMENT_ORDER,
  SUBDIVISIONS,
  type Subdivision,
  HAND_VALUES,
  splitSticking,
  type Hand,
  type DrumInstrument,
} from '../model/types';

interface CellRef {
  readonly button: HTMLButtonElement;
  readonly instrument: DrumInstrument;
  readonly step: number;
}

export class GridView {
  readonly #controller: EditorController;
  readonly #cells: CellRef[] = [];
  readonly #sticking: HTMLSelectElement[] = [];
  /** Elemente je Rasterspalte (Kopfzelle + Instrumentzellen) für die
   * Abspielmarkierung. */
  readonly #columns = new Map<number, HTMLElement[]>();
  #container: HTMLElement | null = null;
  #raster = '';
  #status: HTMLElement | null = null;
  #playheadStep: number | null = null;
  #unsubscribe: (() => void) | null = null;

  constructor(controller: EditorController) {
    this.#controller = controller;
  }

  /** Baut die Ansicht in den angegebenen Container und aktiviert sie. */
  mount(container: HTMLElement): void {
    this.#container = container;
    this.#unsubscribe = this.#controller.subscribe(() => this.#render());
  }

  /** Loest Registrierungen; nuetzlich fuer Tests und Wiederverwendung. */
  destroy(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #buildGrid(): HTMLElement {
    const scroll = document.createElement('div');
    scroll.className = 'grid-scroll';

    const grid = document.createElement('div');
    grid.className = 'grid';
    const subdivisions = this.#controller.snapshot().pattern.subdivisions;
    const beatWidth = Math.max(...subdivisions) * 48;
    grid.style.setProperty(
      '--grid-columns',
      subdivisions
        .map((count) => `repeat(${count}, ${beatWidth / count}px)`)
        .join(' '),
    );
    grid.appendChild(this.#buildRasterRow());

    grid.appendChild(this.#buildHeaderRow());
    for (const instrument of INSTRUMENT_ORDER) {
      grid.appendChild(this.#buildInstrumentRow(instrument));
    }

    grid.appendChild(this.#buildStickingRow());
    scroll.appendChild(grid);
    const status = document.createElement('p');
    status.className = 'raster-status';
    status.setAttribute('role', 'status');
    this.#status = status;
    scroll.appendChild(status);
    return scroll;
  }

  #buildRasterRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'raster-controls';
    const names = {
      2: 'Achtel',
      3: 'Achteltriolen',
      4: 'Sechzehntel',
      6: 'Sechzehnteltriolen',
    };
    const addSelect = (beat: number | null) => {
      const label = document.createElement('label');
      label.textContent = beat === null ? 'Ganzer Takt' : `Viertel ${beat + 1}`;
      const select = document.createElement('select');
      select.setAttribute('aria-label', `Raster ${label.textContent}`);
      const mixed = document.createElement('option');
      mixed.value = '';
      mixed.textContent = 'Gemischt';
      if (beat === null) select.appendChild(mixed);
      for (const count of SUBDIVISIONS) {
        const option = document.createElement('option');
        option.value = String(count);
        option.textContent = names[count];
        select.appendChild(option);
      }
      const values = this.#controller.snapshot().pattern.subdivisions;
      select.value =
        beat === null
          ? values.every((value) => value === values[0])
            ? String(values[0])
            : ''
          : String(values[beat]);
      const previous = select.value;
      select.addEventListener('change', () => {
        if (!select.value) return;
        const error = this.#controller.setSubdivision(
          beat,
          Number(select.value) as Subdivision,
        );
        if (error) {
          select.value = previous;
          if (this.#status) this.#status.textContent = error;
        } else if (this.#status) this.#status.textContent = '';
      });
      label.appendChild(select);
      row.appendChild(label);
    };
    addSelect(null);
    for (let beat = 0; beat < 4; beat++) addSelect(beat);
    return row;
  }

  #positionLabel(step: number): string {
    const beat = Math.floor(step / 4);
    const count = this.#controller.snapshot().pattern.subdivisions[beat]!;
    return `${Math.round(((step - beat * 4) * count) / 4) + 1} von ${count}, Viertel ${beat + 1}`;
  }

  #buildStickingRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'grid-row';
    const label = document.createElement('div');
    label.className = 'grid-label';
    label.textContent = 'Hand';
    label.title =
      'R: rechte Hand · L: linke Hand · RL: beide Hände. Eine Angabe je Rasterposition.';
    row.appendChild(label);
    for (const step of this.#controller.snapshot().pattern.steps) {
      const buildSelect = (values: readonly string[], name: string) => {
        const select = document.createElement('select');
        select.className = 'sticking-select';
        select.setAttribute(
          'aria-label',
          `${name}, Schritt ${this.#positionLabel(step)}, Zählzeit ${Math.floor(step / 4) + 1}`,
        );
        for (const value of values) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value || '–';
          select.appendChild(option);
        }
        return select;
      };
      const hand = buildSelect(HAND_VALUES, 'Hand');
      hand.addEventListener('change', () =>
        this.#controller.setHand(step, hand.value as Hand),
      );
      this.#sticking.push(hand);
      row.appendChild(hand);
    }
    return row;
  }

  #buildHeaderRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'grid-row grid-row--header';
    row.setAttribute('role', 'row');

    const corner = document.createElement('div');
    corner.className = 'grid-label grid-label--corner';
    corner.setAttribute('role', 'columnheader');
    corner.textContent = 'Zählzeit';
    row.appendChild(corner);

    for (const step of this.#controller.snapshot().pattern.steps) {
      const cell = document.createElement('div');
      cell.className = 'grid-count';
      if (step % 4 === 0) {
        cell.classList.add('grid-count--downbeat');
      }
      cell.setAttribute('role', 'columnheader');
      const beat = Math.floor(step / 4);
      const count = this.#controller.snapshot().pattern.subdivisions[beat]!;
      const slot = Math.round(((step - beat * 4) * count) / 4);
      cell.textContent =
        slot === 0
          ? String(beat + 1)
          : count === 4
            ? ['', 'e', '+', 'a'][slot]!
            : count === 2
              ? '+'
              : `${slot + 1}`;
      this.#columns.set(step, [cell]);
      row.appendChild(cell);
    }
    return row;
  }

  #buildInstrumentRow(instrument: DrumInstrument): HTMLElement {
    const row = document.createElement('div');
    row.className = 'grid-row';
    row.setAttribute('role', 'row');

    const label = document.createElement('div');
    label.className = 'grid-label';
    label.setAttribute('role', 'rowheader');
    label.textContent = INSTRUMENT_LABELS[instrument];
    row.appendChild(label);

    for (const step of this.#controller.snapshot().pattern.steps) {
      row.appendChild(this.#buildCell(instrument, step));
    }
    return row;
  }

  #buildCell(instrument: DrumInstrument, step: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'grid-cell';
    if (step % 4 === 0) {
      wrapper.classList.add('grid-cell--downbeat');
    }
    wrapper.setAttribute('role', 'gridcell');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'step';
    button.dataset.instrument = instrument;
    button.dataset.step = String(step);
    button.setAttribute(
      'aria-label',
      `${INSTRUMENT_LABELS[instrument]}, Schritt ${this.#positionLabel(step)}`,
    );
    button.addEventListener('click', () => {
      this.#controller.toggle(instrument, step);
    });

    wrapper.appendChild(button);
    this.#columns.get(step)?.push(wrapper);
    this.#cells.push({ button, instrument, step });
    return wrapper;
  }

  /**
   * Hebt die aktuell gespielte Rasterspalte hervor. Rein visuell – der
   * Pattern-Zustand bleibt unberührt. `null` entfernt die Markierung.
   */
  setPlayheadStep(step: number | null): void {
    if (step !== null) {
      const steps = this.#controller.snapshot().pattern.steps;
      step = [...steps].reverse().find((position) => position <= step!) ?? null;
    }
    if (step === this.#playheadStep) {
      return;
    }
    if (this.#playheadStep !== null) {
      for (const element of this.#columns.get(this.#playheadStep) ?? []) {
        element.classList.remove('is-playing');
      }
    }
    if (step !== null) {
      for (const element of this.#columns.get(step) ?? []) {
        element.classList.add('is-playing');
      }
    }
    this.#playheadStep = step;
  }

  /** Aktualisiert die Darstellung aller Zellen anhand des Controllers. */
  #render(): void {
    const pattern = this.#controller.snapshot().pattern;
    const raster = pattern.subdivisions.join(',');
    if (raster !== this.#raster && this.#container) {
      this.#raster = raster;
      this.#cells.length = 0;
      this.#sticking.length = 0;
      this.#columns.clear();
      this.#playheadStep = null;
      this.#container.replaceChildren(this.#buildGrid());
    }
    this.#sticking.forEach((select, index) => {
      const step = pattern.steps[index]!;
      const value = splitSticking(
        this.#controller.snapshot().pattern.getSticking(step),
      );
      select.value = value.hand;
    });
    for (const cell of this.#cells) {
      const active = this.#controller.isActive(cell.instrument, cell.step);
      cell.button.classList.toggle('step--active', active);
      cell.button.setAttribute('aria-pressed', String(active));
    }
  }
}
